import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScheduleItem, Doctor, Staff, Room } from './SchedulerTypes';
import { Button } from '@/components/ui/button';
import { Clock, User, MapPin, FileText } from 'lucide-react';

interface MultiItemSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: ScheduleItem[];
  onSelectItem: (item: ScheduleItem) => void;
  time: string;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
}

const MultiItemSelectionModal: React.FC<MultiItemSelectionModalProps> = ({
  isOpen,
  onClose,
  items,
  onSelectItem,
  time,
  doctors,
  staff,
  rooms,
}) => {
  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'appointment':
        return 'Appointment';
      case 'admin':
        return 'Admin';
      case 'break':
        return 'Break';
      case 'leave':
        return 'Leave';
      case 'mc':
        return 'MC/Urgent Leave';
      default:
        return type;
    }
  };

  const getItemTypeColor = (type: string) => {
    switch (type) {
      case 'appointment':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'admin':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'break':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'leave':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'mc':
        return 'bg-red-200 text-red-900 border-red-400';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getDoctorName = (doctorId: string | null | undefined) => {
    if (!doctorId) return null;
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? `Dr. ${doctor.short_name || doctor.name}` : null;
  };

  const getStaffName = (staffId: string | null | undefined) => {
    if (!staffId) return null;
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember ? staffMember.short_name || staffMember.name : null;
  };

  const getRoomName = (roomId: string | null | undefined) => {
    if (!roomId) return null;
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : null;
  };

  const handleItemClick = (item: ScheduleItem) => {
    onSelectItem(item);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Multiple Items at {time}</DialogTitle>
          <DialogDescription>
            There are {items.length} overlapping items. Select one to view or edit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {items.map((item, index) => {
            const doctorName = getDoctorName(item.doctorId);
            const staffName = getStaffName(item.staffId);
            const roomName = getRoomName(item.roomId);

            return (
              <Button
                key={item.id}
                variant="outline"
                className={`w-full justify-start h-auto py-3 px-4 text-left border-2 hover:bg-accent ${getItemTypeColor(item.type)}`}
                onClick={() => handleItemClick(item)}
              >
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">
                      {item.title || getItemTypeLabel(item.type)}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-white/50">
                      {getItemTypeLabel(item.type)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs opacity-80">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {item.startTime} - {item.endTime}
                    </span>
                    {(doctorName || staffName) && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {doctorName || staffName}
                      </span>
                    )}
                    {roomName && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {roomName}
                      </span>
                    )}
                  </div>
                  {item.notes && (
                    <div className="flex items-start gap-1 text-xs opacity-70 mt-1">
                      <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{item.notes}</span>
                    </div>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MultiItemSelectionModal;
